// Google AdSense configuration for the browser build.
// Values are injected at build time so native WebViews never need to load AdSense.

export type AdFormat = 'auto' | 'rectangle' | 'vertical' | 'horizontal';

const publisherId = (import.meta.env.VITE_ADSENSE_CLIENT ?? '').trim();

export const AD_SLOTS = {
  LAUNCHER: (import.meta.env.VITE_ADSENSE_LAUNCHER_SLOT ?? '').trim(),
  SIDEBAR: (import.meta.env.VITE_ADSENSE_SIDEBAR_SLOT ?? '').trim(),
} as const;

const isPublisherId = (value: string) => /^ca-pub-\d+$/.test(value);
const isSlotId = (value: string) => /^\d+$/.test(value);

export const hasConfiguredAdSlot = (slot: string): boolean =>
  isPublisherId(publisherId) && isSlotId(slot);

class AdSenseService {
  private loadPromise: Promise<void> | null = null;

  get clientId(): string {
    return publisherId;
  }

  load(): Promise<void> {
    if (typeof window === 'undefined' || !isPublisherId(publisherId)) {
      return Promise.resolve();
    }

    if ((window as Window & { adsbygoogle?: unknown[] }).adsbygoogle) {
      return Promise.resolve();
    }

    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-nebula-adsense]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('AdSense script failed to load')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.dataset.nebulaAdsense = 'true';
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(publisherId)}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('AdSense script failed to load'));
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  async pushAd(): Promise<void> {
    await this.load();
    ((window as Window & { adsbygoogle?: unknown[] }).adsbygoogle ||= []).push({});
  }
}

export const adsenseService = new AdSenseService();
